import type { UserProfile } from '../types';
import { ApiError, apiRequest, setCsrfToken } from './client';

export interface WebAuthenticatedSession {
  profile: UserProfile;
  csrfToken: string;
  expiresAtUtc: string;
}

export interface AuthStep {
  challengeId: string;
  token: string;
  kind: 'twoFactor' | 'enroll';
  expiresIn: number;
}

export interface TrustedDevice {
  deviceId: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  userAgent: string | null;
  ip: string | null;
  deviceName: string | null;
  isCurrent: boolean;
}

export interface WebSession {
  sessionId: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  ip: string | null;
  userAgent: string | null;
  deviceName: string | null;
  isCurrent: boolean;
}

const deviceHeaders = () => ({ 'X-KorsaApp-Device-Name': navigator.platform || 'Web browser' });

// Serialize cookie rotation across tabs where Web Locks is available, and within this tab otherwise.
let authQueue = Promise.resolve();
const withDeviceCookieLock = <T>(action: () => Promise<T>): Promise<T> => {
  if (navigator.locks) return navigator.locks.request('korsaapp-device-session', action);
  const result = authQueue.then(action, action);
  authQueue = result.then(() => undefined, () => undefined);
  return result;
};

export const login = (loginValue: string, password: string) => withDeviceCookieLock(async () => {
  const result = await apiRequest<WebAuthenticatedSession | AuthStep>('/api/auth/login', {
    method: 'POST', authenticated: false, headers: deviceHeaders(), body: { login: loginValue, password },
  });
  if ('kind' in result) return result;
  setCsrfToken(result.csrfToken);
  return result;
});

export const activate = (username: string, activationCode: string, newPassword: string, passwordConfirmation: string) =>
  apiRequest<AuthStep>('/api/auth/activate', {
    method: 'POST', authenticated: false,
    body: { username, activationCode, newPassword, passwordConfirmation },
  });

export const resetPassword = (username: string, resetCode: string, newPassword: string,
  passwordConfirmation: string, totpCode: string) =>
  apiRequest<void>('/api/auth/reset-password', {
    method: 'POST', authenticated: false,
    body: { username, resetCode, newPassword, passwordConfirmation, totpCode },
  });

export const setupTotp = (step: AuthStep) =>
  apiRequest<{ secret: string; otpAuthUri: string }>('/api/auth/2fa/setup', {
    method: 'POST', authenticated: false, body: { challengeId: step.challengeId, token: step.token },
  });

export const enableTotp = (step: AuthStep, code: string) =>
  apiRequest<{ recoveryCodes: string[] }>('/api/auth/2fa/enable', {
    method: 'POST', authenticated: false, body: { challengeId: step.challengeId, token: step.token, code },
  });

export const verifyTotpLogin = (step: AuthStep, code: string, trustDevice: boolean, rememberDevice = false) => withDeviceCookieLock(async () => {
  const result = await apiRequest<WebAuthenticatedSession>('/api/auth/2fa/verify', {
    method: 'POST', authenticated: false, headers: deviceHeaders(),
    body: { challengeId: step.challengeId, token: step.token, code, trustDevice, rememberDevice, deviceName: navigator.platform || 'Web browser' },
  });
  setCsrfToken(result.csrfToken);
  return result;
});

const restoreSessionInternal = async () => {
  const session = await apiRequest<WebAuthenticatedSession>('/api/web-auth/session', { authenticated: false })
    .catch((error: unknown) => {
      if (!(error instanceof ApiError && error.status === 401)) throw error;
      return apiRequest<WebAuthenticatedSession>('/api/web-auth/restore', {
        method: 'POST', authenticated: false, headers: { ...deviceHeaders(), 'X-KorsaApp-Device-Restore': '1' },
      });
    });
  setCsrfToken(session.csrfToken);
  return session;
};

let restoring: Promise<WebAuthenticatedSession> | null = null;
export const restoreSession = () => {
  if (!restoring) restoring = withDeviceCookieLock(restoreSessionInternal).finally(() => { restoring = null; });
  return restoring;
};

export const refreshPersistentDevice = () => withDeviceCookieLock(() =>
  apiRequest<void>('/api/web-auth/persistent-devices/refresh', { method: 'POST' }));
export const listPersistentDevices = () => apiRequest<WebSession[]>('/api/web-auth/persistent-devices');
export const revokePersistentDevice = (sessionId: string) =>
  apiRequest<void>(`/api/web-auth/persistent-devices/${encodeURIComponent(sessionId)}/revoke`, { method: 'POST' });
export const revokeOtherPersistentDevices = () =>
  apiRequest<void>('/api/web-auth/persistent-devices/revoke-others', { method: 'POST' });

export const logout = async () => {
  try {
    await withDeviceCookieLock(async () => {
      const session = await restoreSessionInternal();
      await apiRequest<void>('/api/web-auth/logout', {
        method: 'POST', authenticated: false, headers: { 'X-KorsaApp-CSRF': session.csrfToken },
      });
    });
  }
  finally { setCsrfToken(null); }
};

export const listWebSessions = () => apiRequest<WebSession[]>('/api/web-auth/sessions');
export const revokeWebSession = (sessionId: string) =>
  apiRequest<void>(`/api/web-auth/sessions/${encodeURIComponent(sessionId)}/revoke`, { method: 'POST' });
export const revokeOtherWebSessions = () =>
  apiRequest<void>('/api/web-auth/sessions/revoke-others', { method: 'POST' });
export const listTrustedDevices = () => apiRequest<TrustedDevice[]>('/api/auth/trusted-devices');
export const revokeTrustedDevice = (deviceId: string) =>
  apiRequest<void>(`/api/auth/trusted-devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' });
export const revokeAllTrustedDevices = () =>
  apiRequest<void>('/api/auth/trusted-devices/revoke-all', { method: 'POST' });
export const regenerateRecoveryCodes = (password: string, code: string) =>
  apiRequest<{ recoveryCodes: string[] }>('/api/auth/2fa/recovery-codes/regenerate', {
    method: 'POST', body: { password, code },
  });

export const changePassword = (currentPassword: string, newPassword: string,
  newPasswordConfirmation: string, totpCode: string) =>
  apiRequest<void>('/api/account/change-password', {
    method: 'POST', body: { currentPassword, newPassword, newPasswordConfirmation, totpCode },
  });
